import { ref, computed, readonly } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import i18n from "@documentx/shared/locales";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8010";

const STORAGE_KEYS = {
  token: "admin_auth_token",
  state: "admin_auth_state",
  nonce: "admin_auth_nonce",
  codeVerifier: "admin_auth_code_verifier",
  returnPath: "admin_auth_return_path",
} as const;

interface AuthUser {
  email: string;
  name: string;
}

const user = ref<AuthUser | null>(null);
const token = ref<string | null>(null);
let expiryTimerId: ReturnType<typeof setInterval> | null = null;

function decodeJWTPayload(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isTokenExpired(t: string): boolean {
  const payload = decodeJWTPayload(t);
  if (!payload || typeof payload.exp !== "number") return true;
  return payload.exp * 1000 <= Date.now();
}

function isTokenExpiringSoon(t: string, thresholdMs = 5 * 60 * 1000): boolean {
  const payload = decodeJWTPayload(t);
  if (!payload || typeof payload.exp !== "number") return true;
  return payload.exp * 1000 - Date.now() <= thresholdMs;
}

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(36).padStart(2, "0"))
    .join("")
    .slice(0, length);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function validateReturnPath(path: string | null): string | null {
  if (!path) return null;
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  return path;
}

export function useAuth() {
  const router = useRouter();

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  function startExpiryTimer() {
    stopExpiryTimer();
    expiryTimerId = setInterval(() => {
      const t = token.value;
      if (!t) {
        stopExpiryTimer();
        return;
      }

      if (isTokenExpired(t)) {
        stopExpiryTimer();
        clearAuthState();
        router.replace("/login");
        return;
      }

      if (isTokenExpiringSoon(t)) {
        stopExpiryTimer();
        toast.warning(i18n.global.t('admin.brand.sessionExpiring'), {
          description: i18n.global.t('admin.brand.sessionRenewDesc'),
          action: {
            label: i18n.global.t('admin.brand.sessionRenew'),
            onClick: () => login(router.currentRoute.value.fullPath),
          },
          duration: Infinity,
        });
      }
    }, 60_000);
  }

  function stopExpiryTimer() {
    if (expiryTimerId !== null) {
      clearInterval(expiryTimerId);
      expiryTimerId = null;
    }
  }

  function setAuthState(accessToken: string, u: AuthUser) {
    token.value = accessToken;
    user.value = u;
    sessionStorage.setItem(STORAGE_KEYS.token, accessToken);
    startExpiryTimer();
  }

  function clearAuthState() {
    token.value = null;
    user.value = null;
    stopExpiryTimer();
    for (const key of Object.values(STORAGE_KEYS)) {
      sessionStorage.removeItem(key);
    }
  }

  async function login(returnPath?: string) {
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    sessionStorage.setItem(STORAGE_KEYS.state, state);
    sessionStorage.setItem(STORAGE_KEYS.nonce, nonce);
    sessionStorage.setItem(STORAGE_KEYS.codeVerifier, codeVerifier);

    const validReturn = validateReturnPath(returnPath ?? null);
    if (validReturn) {
      sessionStorage.setItem(STORAGE_KEYS.returnPath, validReturn);
    }

    const params = new URLSearchParams({
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    const resp = await fetch(
      `${API_BASE}/admin/auth/authorization-url?${params}`,
    );
    if (!resp.ok) {
      throw new Error("Failed to get authorization URL");
    }

    const data = await resp.json();
    window.location.href = data.authorizationUrl;
  }

  async function handleCallback(
    code: string,
    state: string,
  ): Promise<{ success: boolean; error?: string }> {
    const storedState = sessionStorage.getItem(STORAGE_KEYS.state);

    if (!storedState) {
      const existingToken = sessionStorage.getItem(STORAGE_KEYS.token);
      if (existingToken && !isTokenExpired(existingToken)) {
        return { success: true };
      }
      return { success: false };
    }

    if (state !== storedState) {
      clearOIDCParams();
      return { success: false, error: "Invalid state parameter" };
    }

    const nonce = sessionStorage.getItem(STORAGE_KEYS.nonce) ?? "";
    const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.codeVerifier) ?? "";

    try {
      const resp = await fetch(`${API_BASE}/admin/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, nonce, code_verifier: codeVerifier }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        clearOIDCParams();
        return {
          success: false,
          error: data.error || "Authentication failed",
        };
      }

      setAuthState(data.accessToken, {
        email: data.email,
        name: data.name,
      });

      const returnPath = validateReturnPath(
        sessionStorage.getItem(STORAGE_KEYS.returnPath),
      );
      clearOIDCParams();

      return { success: true, error: undefined };
    } catch (err) {
      clearOIDCParams();
      return {
        success: false,
        error: "Network error during authentication",
      };
    }
  }

  function getReturnPath(): string {
    return validateReturnPath(sessionStorage.getItem(STORAGE_KEYS.returnPath)) ?? "/";
  }

  function clearOIDCParams() {
    sessionStorage.removeItem(STORAGE_KEYS.state);
    sessionStorage.removeItem(STORAGE_KEYS.nonce);
    sessionStorage.removeItem(STORAGE_KEYS.codeVerifier);
    sessionStorage.removeItem(STORAGE_KEYS.returnPath);
  }

  function logout() {
    clearAuthState();
    router.replace("/login");
  }

  function getToken(): string | null {
    return token.value;
  }

  function initAuth() {
    const stored = sessionStorage.getItem(STORAGE_KEYS.token);
    if (stored && !isTokenExpired(stored)) {
      token.value = stored;
      const payload = decodeJWTPayload(stored);
      if (payload) {
        user.value = {
          email: (payload.email as string) ?? "",
          name: (payload.name as string) ?? "",
        };
      }
      startExpiryTimer();
    } else if (stored) {
      sessionStorage.removeItem(STORAGE_KEYS.token);
    }
  }

  return {
    user: readonly(user),
    isAuthenticated,
    login,
    handleCallback,
    getReturnPath,
    logout,
    getToken,
    initAuth,
  };
}
